import { create } from 'zustand';
import { useZoneStore } from './zoneStore';
import { useNetworkElementsStore } from './networkElementsStore';
import { networkApi } from '../infrastructure/api/NetworkApiClient';
import { applyPowerCalculationResponse, type PowerCalculationResponse } from '../services/powerCalculationMapping';

/**
 * Historical powerflow playback (map time-rewind).
 *
 * Default mode is 'live': the map renders the 10s live poll unchanged.
 * In 'historical' mode a stored powerflow snapshot is fetched from
 * GET /api/zones/{id}/powerflow-snapshots/at and shadow-written into
 * networkElementsStore through the SAME mapping the live poll uses
 * (applyPowerCalculationResponse) — map components stay untouched.
 * While historical, powerFlowService.fetchAndUpdate() early-returns so the
 * live poll cannot overwrite the frame; the timer keeps running, so
 * backToLive() restores the live view within one fetch.
 */

export interface SnapshotRange {
  from: string; // ISO UTC
  to: string; // ISO UTC
  count: number;
}

/**
 * One meter's measured power at the snapshot's calculation instant — the
 * exact input the archived load flow was driven by. powerW is SIGNED
 * (+ Bezug, − Einspeisung). Absent on snapshots archived before 2026-08.
 */
export interface SnapshotMeterValue {
  elementId: string;
  serialNumber: string;
  meloId?: string;
  obisCode?: string;
  powerW: number;
  /** True when the value was estimated, not measured (e.g. PV-Nachbarschätzung). */
  simulated?: boolean;
  /** Origin of the value (e.g. "pv_neighbor_estimate"); measured values omit it. */
  source?: string;
}

interface HistoricalSnapshotResponse extends PowerCalculationResponse {
  computedAt: string;
  meterValues?: SnapshotMeterValue[];
}

/** Netzanschluss check frame (GET …/netzanschluss/requests/{id}/frame) — same wire shape plus direction info. */
interface CheckFrameResponse extends PowerCalculationResponse {
  direction: CheckFrameDirection;
  available: CheckFrameDirection[];
  displayOnly?: CheckFrameDirection[];
  computedAt: string;
}

export type CheckFrameDirection = 'feedIn' | 'peakLoad';

/** What the map is showing while mode === 'check'. */
export interface CheckFrameInfo {
  requestId: string;
  direction: CheckFrameDirection;
  /** Worst-first. */
  available: CheckFrameDirection[];
  /** Directions computed for the map only (the requested units do not act there; no criterion evaluated). */
  displayOnly: CheckFrameDirection[];
  /** Human label of the check, e.g. "PV 15 kW an Anschluss 8dc165". */
  label: string;
}

interface HistoricalPlaybackState {
  /**
   * 'live' = 10 s poll; 'historical' = archived snapshot (time-rewind);
   * 'check' = the with-assets load flow of a Netzanschlussprüfung (Prüffall).
   * Both non-live modes share the live-poll guard and the one mapping path.
   */
  mode: 'live' | 'historical' | 'check';
  checkFrame: CheckFrameInfo | null;
  /** Available snapshot range for the active zone; null while unknown/empty. */
  range: SnapshotRange | null;
  /** Requested instant (ISO UTC). */
  selectedTs: string | null;
  /** Actual instant of the loaded snapshot (ISO UTC, <= selectedTs). */
  effectiveTs: string | null;
  /**
   * Exact per-meter inputs of the loaded snapshot, grouped by Hausanschluss id.
   * Null when the snapshot predates the meterValues field — the meter cards
   * then fall back to the hourly meter archive.
   */
  meterValuesByElement: Record<string, SnapshotMeterValue[]> | null;
  isLoading: boolean;
  error: string | null;

  loadRange: () => Promise<void>;
  setTimestamp: (tsIso: string) => Promise<void>;
  /** Shows the stored with-assets frame of a check on the map (Prüffall). Throws on failure. */
  showCheckFrame: (requestId: string, label: string, direction?: CheckFrameDirection) => Promise<void>;
  backToLive: () => void;
  /** Leaves a check frame the way the banner button does: archive zone → newest snapshot, otherwise live. No-op outside mode 'check'. */
  leaveCheckFrame: () => void;
  /**
   * View isolation between the map (live / rewind), the Netzanschluss workspace
   * and a check: a workspace captures the map's view on entry, shows its OWN base
   * state (live, or the newest snapshot of an archive zone) and puts the captured
   * view back on exit — a check must never leak into the map, a rewind never
   * into a check.
   */
  captureView: () => PlaybackView;
  restoreView: (view: PlaybackView) => void;
  /** The workspace's own base state (no-op when already there). */
  enterWorkspaceView: () => void;
  reset: () => void;
}

/** A user-visible map view worth restoring: live, or a rewind to an instant. */
export interface PlaybackView {
  mode: 'live' | 'historical';
  selectedTs: string | null;
}

// Monotonic request counter — only the newest in-flight snapshot request may
// write into the store (fast scrubbing fires overlapping requests).
let requestSeq = 0;

let rangeInFlight: { zoneId: string; promise: Promise<void> } | null = null;

export const useHistoricalPlaybackStore = create<HistoricalPlaybackState>((set, get) => ({
  mode: 'live',
  checkFrame: null,
  range: null,
  selectedTs: null,
  effectiveTs: null,
  meterValuesByElement: null,
  isLoading: false,
  error: null,

  loadRange: async () => {
    const activeZoneId = useZoneStore.getState().activeZoneId;
    if (!activeZoneId) {
      set({ range: null });
      return;
    }
    // MapOverlay's mount effect and the zone subscription below both ask for the range on a
    // zone switch; one request per zone is enough (it is one of the slower backend queries).
    if (rangeInFlight && rangeInFlight.zoneId === activeZoneId) {
      await rangeInFlight.promise;
      return;
    }
    try {
      const promise = networkApi.zoneGet<{ from?: string; to?: string; count: number }>(
        '/powerflow-snapshots/range'
      );
      rangeInFlight = { zoneId: activeZoneId, promise: promise.then(() => undefined, () => undefined) };
      const data = await promise;
      rangeInFlight = null;
      if (useZoneStore.getState().activeZoneId !== activeZoneId) {
        return; // stale-zone guard
      }
      if (data && data.count > 0 && data.from != null && data.to != null) {
        set({ range: { from: data.from, to: data.to, count: data.count } });
        // Archive-only zones (inactive: no live cycle will ever fill the map)
        // enter playback at the newest snapshot right away — an empty "live"
        // map with a hidden Verlauf button confused every first-time viewer.
        const zone = useZoneStore.getState().zones.find(z => z.id === activeZoneId);
        if (zone && zone.isActive === false && get().mode === 'live') {
          void get().setTimestamp(data.to);
        }
      } else {
        set({ range: null });
      }
    } catch (error) {
      rangeInFlight = null;
      console.warn('[HistoricalPlayback] Range fetch failed:', error);
      set({ range: null });
    }
  },

  setTimestamp: async (tsIso: string) => {
    const activeZoneId = useZoneStore.getState().activeZoneId;
    if (!activeZoneId) {
      return;
    }

    const mySeq = ++requestSeq;
    set({ mode: 'historical', selectedTs: tsIso, isLoading: true, error: null });

    try {
      const data = await networkApi.zoneGet<HistoricalSnapshotResponse>(
        `/powerflow-snapshots/at?ts=${encodeURIComponent(tsIso)}`
      );
      if (useZoneStore.getState().activeZoneId !== activeZoneId) {
        return; // stale-zone guard
      }
      if (mySeq !== requestSeq || get().mode !== 'historical') {
        return; // superseded by a newer request or by backToLive()
      }

      applyPowerCalculationResponse(data);
      let meterValuesByElement: Record<string, SnapshotMeterValue[]> | null = null;
      if (data.meterValues != null) {
        meterValuesByElement = {};
        for (const mv of data.meterValues) {
          (meterValuesByElement[mv.elementId] ??= []).push(mv);
        }
      }
      set({ effectiveTs: data.computedAt, meterValuesByElement, isLoading: false });
    } catch (error: any) {
      if (mySeq !== requestSeq) {
        return;
      }
      const notFound = error?.status === 404;
      console.warn('[HistoricalPlayback] Snapshot fetch failed:', error);
      set({
        isLoading: false,
        error: notFound
          ? 'Für diesen Zeitpunkt liegt kein gespeichertes Lastfluss-Ergebnis vor.'
          : 'Historischer Lastfluss konnte nicht geladen werden.',
      });
    }
  },

  showCheckFrame: async (requestId, label, direction) => {
    const activeZoneId = useZoneStore.getState().activeZoneId;
    if (!activeZoneId) {
      return;
    }
    const mySeq = ++requestSeq;
    // A check frame replaces any rewind: no timestamp, no meter values.
    set({ mode: 'check', selectedTs: null, effectiveTs: null, meterValuesByElement: null, isLoading: true, error: null });
    try {
      const query = direction ? `?direction=${encodeURIComponent(direction)}` : '';
      const data = await networkApi.zoneGet<CheckFrameResponse>(`/netzanschluss/requests/${requestId}/frame${query}`);
      if (useZoneStore.getState().activeZoneId !== activeZoneId) {
        return; // stale-zone guard
      }
      if (mySeq !== requestSeq || get().mode !== 'check') {
        return; // superseded (newer frame request or backToLive)
      }
      applyPowerCalculationResponse(data);
      set({
        checkFrame: { requestId, direction: data.direction, available: data.available, displayOnly: data.displayOnly ?? [], label },
        effectiveTs: data.computedAt,
        isLoading: false,
      });
    } catch (error) {
      if (mySeq !== requestSeq) {
        return;
      }
      set({ isLoading: false, error: 'Prüffall-Lastfluss konnte nicht geladen werden.' });
      throw error;
    }
  },

  captureView: () => {
    const { mode, selectedTs } = get();
    // A check frame is never a view worth restoring — it belongs to the workspace.
    return mode === 'historical' && selectedTs ? { mode, selectedTs } : { mode: 'live', selectedTs: null };
  },

  restoreView: (view) => {
    const { mode, selectedTs } = get();
    if (view.mode === 'historical' && view.selectedTs) {
      if (mode !== 'historical' || selectedTs !== view.selectedTs) {
        void get().setTimestamp(view.selectedTs);
      }
      return;
    }
    // Live view: archive zones have no live cycle — their "live" is the newest snapshot.
    const activeZoneId = useZoneStore.getState().activeZoneId;
    const zone = useZoneStore.getState().zones.find(z => z.id === activeZoneId);
    const range = get().range;
    if (zone && zone.isActive === false && range) {
      if (mode !== 'historical' || selectedTs !== range.to) {
        void get().setTimestamp(range.to);
      }
    } else if (mode !== 'live') {
      get().backToLive();
    }
  },

  enterWorkspaceView: () => {
    // Same rule as the live view above: the workspace starts from live (or the
    // newest snapshot of an archive zone), never from the map's rewind.
    get().restoreView({ mode: 'live', selectedTs: null });
  },

  leaveCheckFrame: () => {
    if (get().mode !== 'check') {
      return;
    }
    // Archive-fed zones have no live cycle — go back to the newest snapshot;
    // everything else returns to live (same rule as the banner's button).
    const activeZoneId = useZoneStore.getState().activeZoneId;
    const zone = useZoneStore.getState().zones.find(z => z.id === activeZoneId);
    const range = get().range;
    if (zone && zone.isActive === false && range) {
      void get().setTimestamp(range.to);
    } else {
      get().backToLive();
    }
  },

  backToLive: () => {
    requestSeq++; // invalidate in-flight historical / check-frame requests
    set({ mode: 'live', checkFrame: null, selectedTs: null, effectiveTs: null, meterValuesByElement: null, isLoading: false, error: null });
    // The store merges power updates — drop the historical frame explicitly so
    // zones without a live cycle don't keep showing it as if it were live.
    useNetworkElementsStore.getState().clearPowerData();
    // Restore the live frame immediately instead of waiting for the next poll
    // tick. Dynamic import avoids a module cycle (powerFlowService reads this
    // store for its historical-mode guard).
    void import('../services/powerFlowService').then(m => m.default.fetchAndUpdate());
  },

  reset: () => {
    requestSeq++;
    set({ mode: 'live', checkFrame: null, range: null, selectedTs: null, effectiveTs: null, meterValuesByElement: null, isLoading: false, error: null });
  },
}));

// Zone switch clears all playback state (same pattern as networkUIStore):
// a rewind in zone A must never survive into zone B.
let lastZoneId: string | null = useZoneStore.getState().activeZoneId;
useZoneStore.subscribe(state => {
  if (state.activeZoneId !== lastZoneId) {
    lastZoneId = state.activeZoneId;
    useHistoricalPlaybackStore.getState().reset();
    if (state.activeZoneId) {
      void useHistoricalPlaybackStore.getState().loadRange();
    }
  }
});
